import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Download,
  Image as ImageIcon,
  Video,
  Filter,
  Search
} from "lucide-react";
import { Input } from "../components/ui/input";

const Downloads = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API}/assets`, { withCredentials: true });
      setAssets(response.data);
    } catch (error) {
      toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (url, filename) => {
    const fullUrl = url.startsWith('/') ? `${window.location.origin}${url}` : url;
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Download started");
  };

  const filteredAssets = assets.filter(asset => {
    if (filter !== "all" && asset.asset_type !== filter) return false;
    return true;
  });

  const images = filteredAssets.filter(a => a.asset_type === "image");
  const videos = filteredAssets.filter(a => a.asset_type === "video");

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="downloads-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-neutral-900">
              Downloads
            </h1>
            <p className="text-neutral-600 mt-1">
              {assets.length} total assets • {images.length} images • {videos.length} videos
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-neutral-500" />
            {["all", "image", "video"].map((f) => (
              <Button
                key={f}
                variant={filter === f ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(f)}
                className={filter === f ? "bg-indigo-600" : ""}
              >
                {f === "all" ? "All" : f === "image" ? "Images" : "Videos"}
              </Button>
            ))}
          </div>
        </div>

        {/* Assets */}
        {filteredAssets.length === 0 ? (
          <Card className="border-neutral-100">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ImageIcon className="w-8 h-8 text-neutral-400" />
              </div>
              <p className="text-neutral-600">No assets found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Images */}
            {filter !== "video" && images.length > 0 && (
              <Card className="border-neutral-100">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-display text-xl flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-indigo-600" />
                    Images ({images.length})
                  </CardTitle>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      images.forEach((img, i) => {
                        setTimeout(() => handleDownload(img.asset_url, `image_${img.asset_id}.png`), i * 100);
                      });
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download All Images
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {images.map((img) => (
                      <div key={img.asset_id} className="group relative">
                        <img 
                          src={img.asset_url?.startsWith('/') ? `${window.location.origin}${img.asset_url}` : img.asset_url}
                          alt="Generated"
                          className="w-full aspect-[3/4] object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <Button 
                            size="sm"
                            onClick={() => handleDownload(img.asset_url, `image_${img.asset_id}.png`)}
                            className="bg-white text-neutral-900 hover:bg-neutral-100"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Videos */}
            {filter !== "image" && videos.length > 0 && (
              <Card className="border-neutral-100">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-display text-xl flex items-center gap-2">
                    <Video className="w-5 h-5 text-violet-600" />
                    Videos ({videos.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {videos.map((vid) => (
                      <div key={vid.asset_id} className="relative bg-neutral-900 rounded-lg overflow-hidden aspect-video">
                        {vid.asset_url?.includes('placehold') ? (
                          <div className="absolute inset-0 flex items-center justify-center text-white">
                            <Video className="w-12 h-12 opacity-50" />
                          </div>
                        ) : (
                          <video 
                            src={vid.asset_url?.startsWith('/') ? `${window.location.origin}${vid.asset_url}` : vid.asset_url}
                            controls
                            className="w-full h-full object-cover"
                          />
                        )}
                        <Button 
                          size="sm"
                          onClick={() => handleDownload(vid.asset_url, `video_${vid.asset_id}.mp4`)}
                          className="absolute bottom-3 right-3 bg-white text-neutral-900 hover:bg-neutral-100"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Downloads;
